import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, readApiError } from '../../lib/api';
import { imageUrl } from '../../lib/format';
import { IntegrationsPanel } from './IntegrationsPanel';
import { NotificationsLog } from '../notifications/NotificationsLog';

interface BusinessConfig {
  legalName: string;
  tradeName: string;
  nit: string;
  address: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  primaryColor: string;
  accentColor: string;
  youtubeVideoId: string | null;
  invoiceResolution: string | null;
  invoicePrefix: string | null;
  invoiceFromNumber: number | null;
  invoiceToNumber: number | null;
  abandonmentDays: number;
  logoPath: string | null;
}

type Tab = 'business' | 'integrations' | 'notifications';

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('business');

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Configuración</h1>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          className={tab === 'business' ? 'btn btn-primary' : 'btn'}
          onClick={() => setTab('business')}
        >
          Negocio
        </button>
        <button
          className={tab === 'integrations' ? 'btn btn-primary' : 'btn'}
          onClick={() => setTab('integrations')}
        >
          Integraciones
        </button>
        <button
          className={tab === 'notifications' ? 'btn btn-primary' : 'btn'}
          onClick={() => setTab('notifications')}
        >
          Notificaciones
        </button>
      </div>

      {tab === 'business' && <BusinessTab />}
      {tab === 'integrations' && <IntegrationsPanel />}
      {tab === 'notifications' && <NotificationsLog />}
    </section>
  );
}

function BusinessTab() {
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: ['settings', 'business-config'],
    queryFn: async () => {
      const { data } = await api.get<BusinessConfig>('/api/settings/business-config');
      return data;
    },
  });

  const [form, setForm] = useState<Partial<BusinessConfig>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | undefined>();

  useEffect(() => {
    if (cfg.data) {
      setForm(cfg.data);
      setPreview(imageUrl(cfg.data.logoPath));
    }
  }, [cfg.data]);

  useEffect(() => {
    if (!logoFile) return;
    const url = URL.createObjectURL(logoFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.patch('/api/settings/business-config', form);
      if (logoFile) {
        const fd = new FormData();
        fd.set('logo', logoFile);
        await api.post('/api/settings/business-config/logo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
    },
    onSuccess: () => {
      setSuccess(true);
      setLogoFile(null);
      setTimeout(() => setSuccess(false), 3000);
      void qc.invalidateQueries({ queryKey: ['settings', 'business-config'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  if (cfg.isLoading) return <p>Cargando...</p>;
  if (cfg.isError || !cfg.data) return <p className="error">No se pudo cargar la configuración.</p>;

  const set = <K extends keyof BusinessConfig>(k: K, v: BusinessConfig[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="card" style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0 }}>Datos del negocio</h2>

      {error && <p className="error">{error}</p>}
      {success && <p style={{ color: 'var(--color-accent)' }}>Guardado correctamente.</p>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '1.5rem' }}>
        <div>
          <div className="field">
            <label>Razón social</label>
            <input className="input" value={form.legalName ?? ''} onChange={(e) => set('legalName', e.target.value)} />
          </div>
          <div className="field">
            <label>Nombre comercial</label>
            <input className="input" value={form.tradeName ?? ''} onChange={(e) => set('tradeName', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="field">
              <label>NIT</label>
              <input className="input" value={form.nit ?? ''} onChange={(e) => set('nit', e.target.value)} />
            </div>
            <div className="field">
              <label>País (ISO-2)</label>
              <input className="input" maxLength={2} value={form.country ?? ''} onChange={(e) => set('country', e.target.value.toUpperCase())} />
            </div>
          </div>
          <div className="field">
            <label>Dirección</label>
            <input className="input" value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="field">
              <label>Ciudad</label>
              <input className="input" value={form.city ?? ''} onChange={(e) => set('city', e.target.value)} />
            </div>
            <div className="field">
              <label>Teléfono</label>
              <input className="input" value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Email</label>
            <input className="input" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
          </div>
        </div>
        <div>
          <label style={{ fontSize: '0.875rem', color: 'var(--color-muted)' }}>Logo</label>
          <div
            style={{
              width: '100%',
              aspectRatio: '1 / 1',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius)',
              marginTop: '0.35rem',
              backgroundImage: preview ? `url(${preview})` : undefined,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--color-muted)',
              fontSize: '0.85rem',
            }}
          >
            {!preview && 'Sin logo'}
          </div>
          <input
            type="file"
            accept="image/*"
            style={{ marginTop: '0.5rem', width: '100%' }}
            onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Branding</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
        <div className="field">
          <label>Color primario</label>
          <input className="input" type="color" value={form.primaryColor ?? '#1a1a1a'} onChange={(e) => set('primaryColor', e.target.value)} />
        </div>
        <div className="field">
          <label>Color de acento</label>
          <input className="input" type="color" value={form.accentColor ?? '#c9a227'} onChange={(e) => set('accentColor', e.target.value)} />
        </div>
        <div className="field">
          <label>YouTube ID</label>
          <input className="input" value={form.youtubeVideoId ?? ''} onChange={(e) => set('youtubeVideoId', e.target.value)} />
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Facturación</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <div className="field">
          <label>Resolución DIAN</label>
          <input className="input" value={form.invoiceResolution ?? ''} onChange={(e) => set('invoiceResolution', e.target.value || null)} />
        </div>
        <div className="field">
          <label>Prefijo</label>
          <input className="input" value={form.invoicePrefix ?? ''} onChange={(e) => set('invoicePrefix', e.target.value || null)} />
        </div>
        <div className="field">
          <label>Desde</label>
          <input className="input" type="number" value={form.invoiceFromNumber ?? ''} onChange={(e) => set('invoiceFromNumber', e.target.value ? Number(e.target.value) : null)} />
        </div>
        <div className="field">
          <label>Hasta</label>
          <input className="input" type="number" value={form.invoiceToNumber ?? ''} onChange={(e) => set('invoiceToNumber', e.target.value ? Number(e.target.value) : null)} />
        </div>
      </div>

      <div className="field">
        <label>Días para abandono (Ley 1480)</label>
        <input
          className="input"
          type="number"
          min={1}
          max={720}
          value={form.abandonmentDays ?? 90}
          onChange={(e) => set('abandonmentDays', Number(e.target.value))}
          style={{ maxWidth: 120 }}
        />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button
          className="btn btn-primary"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}
